import React, { type ComponentPropsWithoutRef } from "react";
import PillarTableColumnChild from "shared/components/pillar-table/cell-content/PillarTableColumnChild";
import {
  type PathToArray,
  type UnwrapArray,
  type DeepValue,
  pillarTableColumnArrayValue,
  pillarTableColumnArrayMapOutput,
} from "shared/components/pillar-table/columns/array/PillarTableColumnArray";
import type { PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import type { DeepKeys } from "shared/types/deepkeys";

export const PillarTableColumnArrayJoin = <T,>({
  cellValuePath,
  arraySeperator,
  joinPath,
  children,
  outputPath,
  itemColumn,
}: Omit<PillarTableBaseColumnCellValuePathProps<T>, "cellValuePath"> & {
  cellValuePath?: PathToArray<T>;
  arraySeperator?: string | PillarTableSafeAny;
  /**
   * @deprecated Use outputPath instead.
   */
  joinPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  outputPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  /**
   * Column rendered once per array item (after outputPath is applied), joined by arraySeperator.
   * Accepts a column element (e.g. <PillarTable.Column.ProfileDisplayName />) or a custom report column config.
   */
  itemColumn?: React.ReactElement | PillarTableSafeAny;
} & ComponentPropsWithoutRef<"div">) => {
  const rowContext = usePillarTableRowContext();
  const arrayValue = pillarTableColumnArrayValue({
    rowContext,
    cellValuePath,
  });

  if (itemColumn !== undefined) {
    const itemSeperator =
      typeof arraySeperator === "string" ? arraySeperator : ", ";
    const items = pillarTableColumnArrayMapOutput({
      arrayValue,
      outputPath: outputPath ?? joinPath,
    }).filter((item: unknown) => item != null);
    const isItemElement = React.isValidElement(itemColumn);
    return (
      <>
        {items.map((item: unknown, index: number) => (
          <React.Fragment key={index}>
            {index > 0 && itemSeperator}
            <PillarTableColumnChild
              rowValueOverride={item}
              rowIndex={rowContext?.rowIndex}
              columnConfig={
                isItemElement ? undefined : (itemColumn as PillarTableSafeAny)
              }
            >
              {isItemElement ? itemColumn : undefined}
            </PillarTableColumnChild>
          </React.Fragment>
        ))}
      </>
    );
  }

  if (children === undefined && arraySeperator === undefined) {
    arraySeperator = ", ";
  }
  if (children !== undefined) {
    arraySeperator = children;
  }

  const newArrayValue = pillarTableColumnArrayMapOutput({
    arrayValue,
    outputPath: outputPath ?? joinPath,
  });

  if (typeof arraySeperator === "string") {
    const stringValues = newArrayValue.filter(
      (v: unknown) =>
        v != null && (typeof v === "string" || typeof v === "number"),
    );
    return <>{stringValues.join(arraySeperator)}</>;
  }
  return (
    <PillarTableColumnChild
      rowValueOverride={newArrayValue}
      rowIndex={rowContext?.rowIndex}
      columnConfig={
        React.isValidElement(arraySeperator)
          ? undefined
          : (arraySeperator as PillarTableSafeAny)
      }
    >
      {arraySeperator}
    </PillarTableColumnChild>
  );
};
